import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';

interface MarkdownTextProps {
  content: string;
  className?: string;
}

// Stable references at module scope to avoid defeating React.memo
const remarkPlugins = [remarkGfm];

const markdownComponents = {
  h1: ({ className, ...props }: any) => (
    <h1 className={cn("text-2xl font-bold mb-4 first:mt-0 mt-6", className)} {...props} />
  ),
  h2: ({ className, ...props }: any) => (
    <h2 className={cn("text-xl font-bold mb-3 first:mt-0 mt-5", className)} {...props} />
  ),
  h3: ({ className, ...props }: any) => (
    <h3 className={cn("text-lg font-semibold mb-2 first:mt-0 mt-4", className)} {...props} />
  ),
  h4: ({ className, ...props }: any) => (
    <h4 className={cn("text-base font-semibold mb-2 first:mt-0 mt-3", className)} {...props} />
  ),
  p: ({ className, ...props }: any) => (
    <p className={cn("mb-4 last:mb-0", className)} {...props} />
  ),
  a: ({ className, ...props }: any) => (
    <a className={cn("text-primary underline hover:text-primary/80", className)} {...props} />
  ),
  blockquote: ({ className, ...props }: any) => (
    <blockquote className={cn("border-l-4 border-border pl-4 italic my-4", className)} {...props} />
  ),
  ul: ({ className, ...props }: any) => (
    <ul className={cn("list-disc mb-4 space-y-1 pl-6", className)} {...props} />
  ),
  ol: ({ className, ...props }: any) => (
    <ol className={cn("list-decimal mb-4 space-y-1 pl-6", className)} {...props} />
  ),
  li: ({ className, ...props }: any) => (
    <li className={cn("pl-2", className)} {...props} />
  ),
  hr: ({ className, ...props }: any) => (
    <hr className={cn("my-4 border-border", className)} {...props} />
  ),
  table: ({ className, ...props }: any) => (
    <table className={cn("w-full border-collapse border border-border my-4", className)} {...props} />
  ),
  th: ({ className, ...props }: any) => (
    <th className={cn("border border-border px-4 py-2 bg-muted font-semibold text-left", className)} {...props} />
  ),
  td: ({ className, ...props }: any) => (
    <td className={cn("border border-border px-4 py-2", className)} {...props} />
  ),
  tr: ({ className, ...props }: any) => (
    <tr className={cn("", className)} {...props} />
  ),
  code: ({ className, ...props }: any) => (
    <code className={cn("bg-muted px-1.5 py-0.5 rounded text-sm font-mono", className)} {...props} />
  ),
  pre: ({ className, ...props }: any) => (
    <pre className={cn("bg-muted p-4 rounded-lg overflow-x-auto my-4 [&>code]:block [&>code]:p-0 [&>code]:bg-transparent", className)} {...props} />
  ),
};

export const MarkdownText = React.memo<MarkdownTextProps>(({ content, className }) => {
  return (
    <div className={cn("prose prose-sm max-w-none dark:prose-invert", className)}>
      <ReactMarkdown remarkPlugins={remarkPlugins} components={markdownComponents}>
        {content}
      </ReactMarkdown>
    </div>
  );
});
MarkdownText.displayName = 'MarkdownText';
